import { computeClikArtResultsWasm, initWasmModule } from '@/wasm/wasm' // WASM calls
import { computeClikArtResultsJS } from '@/lib/clikart/compute-clikart-results'
import * as Sentry from '@sentry/react'
import type { BoundaryComputeMode } from './types/boundary-compute-mode'
import type { BoundaryComputeOptions } from './types/boundary-compute-options'

const DEFAULT_HOT_MS = 120
let lastCallAt = -Infinity

function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

function shouldUseWasm(opts?: BoundaryComputeOptions): boolean {
  const mode: BoundaryComputeMode = opts?.mode ?? 'adaptive'

  if (mode === 'js') return false
  if (mode === 'wasm') return true

  const hotMs = opts?.hotMs ?? DEFAULT_HOT_MS
  const t = nowMs()
  const isHot = t - lastCallAt < hotMs

  lastCallAt = t

  // Typing is bursty; use JS during bursts and try WASM only when infrequent.
  return !isHot
}

/**
 * Initializes WASM for ClikArt computations.
 * Call once at app start.
 */
export async function initClikArtWasm(): Promise<void> {
  try {
    await initWasmModule()
  } catch (error) {
    console.warn('Failed to initialize ClikArt WASM, will fallback to JS:', error)
  }
}

/**
 * Boundary function choosing JS/WASM implementation for ClikArt computation,
 * with Sentry telemetry and fallback support.
 *
 * @param query - Search/filter string
 * @param items - Array of ClikArt objects ({ name: string })
 * @returns Promise of computed/filter results
 */
export async function getClikArtResults(
  query: string,
  items: Array<{ name: string }>,
  opts?: BoundaryComputeOptions
): Promise<Array<{ name: string }>> {
  // Setup Sentry span as in rulerBoundary
  return Sentry.startSpan(
    {
      name: 'clikart.compute',
      op: 'compute',
      attributes: { query, mode: opts?.mode ?? 'adaptive' },
    },
    async () => {
      try {
        if (shouldUseWasm(opts)) {
          // Single WASM call for computation
          const result = await computeClikArtResultsWasm(query, items)

          Sentry.setContext('wasm', {
            success: true,
            implementation: 'go-wasm-clikart',
          })

          return result
        }

        // JS fast path for hot bursts (typing)
        return computeClikArtResultsJS(query, items)
      } catch (error) {
        // WASM failed; Sentry fallback block
        if ((opts?.mode ?? 'adaptive') !== 'js') {
          console.warn('ClikArt WASM failed, falling back to JS:', error)
          Sentry.captureException(error)
          Sentry.setContext('wasm', {
            success: false,
            error: (error as Error).message,
            fallback: 'js-clikart',
          })
        }
      }

      // JS fallback/case

      const jsStart = performance.now()
      const result = computeClikArtResultsJS(query, items)
      const jsDuration = performance.now() - jsStart

      Sentry.setContext('clikart_performance', {
        path: 'js-fallback',
        jsDuration,
        query,
      })

      return result
    }
  )
}
